/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useEffect, useCallback, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import { DataType, toggleClass } from '@grapecity/wijmo';
import { DataMap, CellType } from '@grapecity/wijmo.grid';
import { Button } from '@mui/material';
import {
  getCommonCodeHeader,
  getCommonCodeNames,
  getCommonCodes,
  setCommonCodeGroups,
  setCommonCodes,
} from 'apis/admin/CommonCode';
import { CommonCode, CommonCodeHeader } from 'models/admin/CommonCode';
import { CommonYN } from 'models/common/Common';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import { CrudCode } from 'models/common/Edit';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { Code, commonYNList } from 'models/common/CommonCode';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import CustomGrid from 'components/grids/CustomGrid';
import { useMessageModalStore } from 'stores/useMessageModalStore';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import { getExcelFileName } from 'utils/ExcelUtil';

interface Props {
  hasAuth?: boolean;
  onRefresh: () => void;
}

const MpCodeLv1Grid = ({ hasAuth, onRefresh }: Props) => {
  const { t } = useTranslation();
  const gridRef = useRef<any>();
  const [flexRef, setflexRef] = useState<any>();
  const [rowData, setRowData] = useState<CommonCode[]>([]);
  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();
  const { setMessageModalStateWhenModalOpen } = useMessageModalStore();

  useEffect(() => {
    getCommonCode();
  }, []);

  const getCommonCode = async () => {
    const mpImprTypeLv1: CommonCode[] = await getCommonCodes('MP_IMPR_TYPE_LVL1'); // MP 분류
    setRowData(mpImprTypeLv1);
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        width: 45,
        isReadOnly: true,
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'cmnGrCd',
        visible: false,
      },
      {
        binding: 'cmnCd',
        header: String(t('admin.grid.코드', '코드')),
        align: 'center',
        width: 120,
        isReadOnly: true,
      },
      {
        binding: 'cmnCdNm',
        header: String(t('admin.grid.개선목적명', '개선목적명')),
        align: 'left',
        minWidth: 120,
        width: '*',
        cssClass: 'WijmoPopup',
        isReadOnly: !hasAuth,
        cellTemplate: (params) =>
          params.item['crudKey'] === CrudCode.CREATE
            ? params.value
            : `<span>${params.value}</span><Button id="${hasAuth ? 'btnCode' : ''}" />`,
      },
      {
        binding: 'msgCtn',
        visible: false,
      },
      {
        binding: 'sortOrd',
        header: String(t('admin.grid.순서', '순서')),
        width: 80,
        dataType: DataType.Number,
        align: 'center',
        isReadOnly: !hasAuth,
      },
      {
        binding: 'useYn',
        header: String(t('com.label.사용여부', '사용여부')),
        width: 80,
        align: 'center',
        dataMap: new DataMap(commonYNList, 'cmnCd', 'cmnCdNm'),
        isReadOnly: !hasAuth,
      },
    ];
  }, [rowData]);

  const btnExcelExport = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(flexRef, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });
    book.sheets[0].name = '개선목적Lv1';
    book.saveAsync(getExcelFileName(t('admin.label.MP개선목적Lv1', 'MP개선목적Lv1')));
  };

  const btnAddRow = () => {
    const cmnCd = rowData
      .map((data) => Number(data.cmnCd?.match(/\d+/)?.at(0)))
      .filter((data) => data !== undefined && data !== null && !Number.isNaN(data))
      .sort((a, b) => a - b);

    let index = 0;
    const findCmnCdNumber = cmnCd.find((currentValue, i) => {
      const missingNumber = currentValue !== i + 1;
      if (missingNumber) {
        index = i + 1;
      }
      return missingNumber;
    });

    const maxCmnCd = findCmnCdNumber
      ? String(Number(index)).padStart(3, '0')
      : String(Number(cmnCd.pop()) + Number(1)).padStart(3, '0');

    const newRow = {
      crudKey: CrudCode.CREATE,
      useYn: CommonYN.Y,
      cmnCd: 'IMP' + maxCmnCd,
      cmnGrCd: 'MP_IMPR_TYPE_LVL1',
    } as CommonCode;

    setRowData([newRow, ...rowData]);
  };

  const btnDelRow = useCallback(() => {
    // const selectedRowNodes = flexRef.selectedRows;

    const isSelected = gridRef.current.rows.filter((item) => item.isSelected);

    if (isSelected.length < 1) {
      openMessageBar({
        type: 'error',
        content: t('mp.label.삭제할 행을 선택해 주세요.', '삭제할 행을 선택해 주세요.'),
      });
      return false;
    } else {
      if (isSelected.length < 1) return;
      isSelected.forEach((row) => {
        const item = row.dataItem;
        if (item.crudKey === CrudCode.CREATE) {
          gridRef.current.collectionView.remove(item);
        } else {
          item.crudKey = CrudCode.UPDATE;
          item.useYn = 'N';
        }
      });
      gridRef.current.refresh();
      // const selectedIds = isSelected
      //   .map((rowNode) => {
      //     return parseInt(rowNode._idx!);
      //   })
      //   .reverse();

      // selectedIds.forEach((idx) => {
      //   if (rowData[idx].crudKey === CrudCode.CREATE) {
      //     delete rowData[idx];
      //   } else {
      //     rowData[idx].useYn = 'N';
      //     rowData[idx].crudKey = CrudCode.UPDATE;
      //   }
      // });

      // const filteredData = rowData.filter((element) => element !== undefined);
      // setRowData(filteredData);
    }
    // 행추가된 건만 삭제 가능
    // const selectedRows = gridRef.current.rows.filter(
    //   (r) => r.isSelected && r.dataItem.crudKey === CrudCode.CREATE
    // );

    // const selectedIds = selectedRows
    //   .map((row) => {
    //     return parseInt(row.index!);
    //   })
    //   .reverse();

    // selectedIds.forEach((item) => {
    //   delete rowData[item];
    // });

    // const filteredData = rowData.filter((element) => element !== undefined);
    // setRowData(filteredData);
  }, [rowData, gridRef]);

  const btnSave = async () => {
    const valid = rowData
      .map((rowNode, index) => {
        if (rowNode.cmnCd == null || rowNode.cmnCd == '')
          return `${index + 1} : ${t(
            'admin.msg.코드를 입력해 주세요.',
            '공통코드명을 입력해 주세요.'
          )}`;
        if (rowNode.cmnCdNm == null || rowNode.cmnCdNm == '')
          return `${index + 1} : ${t(
            'admin.msg.코드명을 입력해 주세요.',
            '코드명을 입력해 주세요.'
          )}`;
        if (rowNode.sortOrd == null || rowNode.sortOrd == '') rowNode.sortOrd = '0';
      })
      .filter((element) => element !== undefined);
    if (valid.length) {
      openMessageBar({ type: 'error', content: valid.toString() });
      return false;
    }

    const saveRows = rowData
      .map((rowNode) => {
        return rowNode.crudKey ? rowNode : null;
      })
      .filter((element) => element !== null) as CommonCode[];

    if (saveRows.length == 0) {
      openMessageBar({
        type: 'error',
        content: t('com.label.수정된 항목이 없습니다.', '수정된 항목이 없습니다.'),
      });
      return true;
    }
    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
      yesCallback: () => {
        setCommonCodes(saveRows).then((result) => {
          if (result.data.type === 'error') {
            openMessageBar({
              type: 'error',
              content:
                'Code: ' +
                result.data.code +
                ' ' +
                t('com.label.중복된 코드가 존재합니다.', '중복된 코드가 존재합니다.'),
            });
            return false;
          }
          if (result.data.type === 'used') {
            openMessageBar({
              type: 'error',
              content:
                'Code: ' +
                result.data.code +
                ' ' +
                t(
                  'com.label.사용중인 코드입니다. 삭제할 수 없습니다.',
                  '사용중인 코드입니다. 삭제할 수 없습니다.'
                ),
            });
            return false;
          }
          if (result.data.type === 'confirm') {
            openMessageBar({
              type: 'confirm',
              content: t('com.label.저장되었습니다.', '저장되었습니다.'),
            });
          }
          gridRef.current.refresh();
          onRefresh();
          getCommonCode();
        });
      },
      noCallback: () => {
        return false;
      },
    });
  };

  const onInitialized = (grid) => {
    gridRef.current = grid;
    setflexRef(grid);

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      const item = grid.rows[ht.row].dataItem;

      if (e.target instanceof HTMLButtonElement) {
        switch (e.target.id) {
          // 공통코드
          case 'btnCode':
            setMessageModalStateWhenModalOpen(item.msgCtn, item.cmnCdNm, (cmnCdNm?: string) => {
              if (cmnCdNm && item) {
                setCommonCodeGroups([{ ...item, cmnCdNm: cmnCdNm, crudKey: CrudCode.UPDATE }]).then(
                  (result) => result && result && getCommonCode()
                );
              }
              openMessageBar({
                type: 'confirm',
                content: t('com.label.저장되었습니다.', '저장되었습니다.'),
              });
            });
        }
      }

      // if (e.target instanceof HTMLInputElement && e.target.type === 'checkbox') {
      //   if (e.srcElement.checked === true) {
      //     setMpLv1CmnCd(item.cmnCd);
      //   }
      // }
    });
  };

  const beginningEdit = useEvent((grid, e) => {
    const col = grid.columns[e.col];
    const binding = col.binding;
    const item = grid.rows[e.row].dataItem;

    if (item.crudKey !== CrudCode.CREATE) {
      if (['cmnCd'].includes(binding)) {
        e.cancel = true;
      }
    }
  });

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;

      if (item.crudKey !== CrudCode.CREATE) {
        if (['cmnCd'].includes(binding)) {
          cell.ariaReadOnly = true;
        }
      }
    }
  });

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('admin.label.개선목적 Level1', '개선목적 Level1')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{rowData?.length ?? 0}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          {hasAuth && (
            <Button css={IconButton.button} className="addRow" disableRipple onClick={btnAddRow}>
              {t('com.button.행추가', '행추가')}
            </Button>
          )}
          {hasAuth && (
            <Button css={IconButton.button} className="delRow" disableRipple onClick={btnDelRow}>
              {t('com.button.행삭제', '행삭제')}
            </Button>
          )}
          <Button css={IconButton.button} className="refresh" onClick={getCommonCode} disableRipple>
            {t('com.button.새로고침', '새로고침')}
          </Button>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnExcelExport}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={230}
        deferResizing={false}
        initialized={onInitialized}
        beginningEdit={beginningEdit}
        itemFormatter={onItemFormatter}
        isSelector={true}
        isFilter={true}
        allowPinning={false}
      />
      <GlobalBtnGroup>
        {hasAuth && (
          <Button css={IconButton.button} className="save" disableRipple onClick={btnSave}>
            {t('com.button.저장', '저장')}
          </Button>
        )}
      </GlobalBtnGroup>
    </>
  );
};

export default MpCodeLv1Grid;
